import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import IntegrationActivityLog from "../IntegrationActivityLog";

type Step = 1 | 2 | 3 | 4;

interface StripeConfig {
  configured: boolean;
  hasSecretKey?: boolean;
  hasWebhookSecret?: boolean;
  secretKeyHint?: string;
  publishableKey?: string;
  defaultSuccessUrl?: string;
  defaultCancelUrl?: string;
  enableApplePay?: boolean;
  enableGooglePay?: boolean;
  displayName?: string;
}

interface TestResult {
  valid: boolean;
  mode?: "live" | "test";
  error?: string;
}

const WEBHOOK_URL = `${import.meta.env.VITE_MESSAGING_API_URL ?? ""}/webhooks/stripe`;

export default function StripePayments() {
  const navigate = useNavigate();
  const api = useApi();

  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState<StripeConfig | null>(null);
  const [step, setStep] = useState<Step>(1);

  // Step 1 fields
  const [secretKey, setSecretKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Step 2 fields
  const [publishableKey, setPublishableKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [displayName, setDisplayName] = useState("Stripe");
  const [successUrl, setSuccessUrl] = useState("");
  const [cancelUrl, setCancelUrl] = useState("");
  const [enableApplePay, setEnableApplePay] = useState(true);
  const [enableGooglePay, setEnableGooglePay] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.admin.get("Stripe/Config").then(async (r) => {
      try {
        const d = await r.json() as StripeConfig;
        setExisting(d);
        if (d.configured) {
          setPublishableKey(d.publishableKey ?? "");
          setSuccessUrl(d.defaultSuccessUrl ?? "");
          setCancelUrl(d.defaultCancelUrl ?? "");
          setEnableApplePay(d.enableApplePay ?? true);
          setEnableGooglePay(d.enableGooglePay ?? true);
          setDisplayName(d.displayName ?? "Stripe");
        }
      } catch { /* ignore */ }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function handleTest() {
    if (!secretKey.trim()) { setError("Enter your Stripe Secret Key."); return; }
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const r = await api.admin.post("Stripe/Config/Test", { secretKey: secretKey.trim() });
      const d = await r.json() as TestResult;
      setTestResult(d);
      if (d.valid) {
        setStep(2);
      } else {
        setError(d.error ?? "Connection test failed.");
      }
    } catch {
      setError("Could not reach Stripe. Check your connection.");
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const r = await api.admin.post("Stripe/Config", {
        secretKey: secretKey.trim() || undefined,
        publishableKey: publishableKey.trim(),
        webhookSecret: webhookSecret.trim() || undefined,
        displayName: displayName.trim(),
        defaultSuccessUrl: successUrl.trim(),
        defaultCancelUrl: cancelUrl.trim(),
        enableApplePay,
        enableGooglePay
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({})) as { error?: string };
        setError(b?.error ?? "Failed to save.");
        return;
      }
      setStep(4);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>;

  const steps = [
    { n: 1, label: "Secret Key" },
    { n: 2, label: "Settings" },
    { n: 3, label: "Webhook" },
    { n: 4, label: "Done" }
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-800">Stripe Payments</h1>
        <p className="text-sm text-slate-500 mt-1">
          Connect Stripe to enable in-chat payment requests. On Apple Messages for Business,
          customers pay with Apple Pay without leaving iMessage.
        </p>
      </div>

      {/* Existing config banner */}
      {existing?.configured && step === 1 && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-3">
          <span className="text-xl">✓</span>
          <div>
            <div className="font-semibold">Stripe is connected</div>
            <div className="text-xs text-green-700">Secret key: {existing.secretKeyHint} — update below to change</div>
          </div>
        </div>
      )}

      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? "bg-cornflower-blue-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > s.n ? "✓" : s.n}
              </div>
              <span className={`text-sm ${step === s.n ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-3" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 1: Secret Key ── */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-base font-semibold text-slate-800 mb-1">Enter your Stripe Secret Key</h2>
          <p className="text-sm text-slate-500 mb-5">Find it in the Stripe Dashboard under Developers → API Keys.</p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-xs text-blue-800 space-y-1">
            <p className="font-medium text-sm">Why Apple Pay works automatically</p>
            <p>
              Stripe's hosted Checkout page enables Apple Pay on Safari/iPhone automatically — no separate
              Apple Pay merchant registration required. Your customers see a native Apple Pay button
              when paying from an iPhone or Mac.
            </p>
          </div>

          <label className="flex flex-col gap-1 mb-5">
            <span className="text-sm font-medium">Secret Key</span>
            <input
              type="password"
              className="input font-mono text-sm"
              placeholder={existing?.configured ? "Enter new key to update (leave blank to keep existing)" : "sk_live_... or sk_test_..."}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              autoFocus={!existing?.configured}
            />
            <span className="text-xs text-slate-400">
              Use <code className="bg-slate-100 px-1 rounded">sk_test_</code> for testing.
              Never share your secret key publicly.
            </span>
          </label>

          {testResult?.valid && (
            <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              Connection verified — {testResult.mode === "live" ? "Live mode (real payments)" : "Test mode"}
            </div>
          )}

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-between items-center">
            <SecondaryBlueButton label="Cancel" onClick={() => navigate("/integrations")} />
            <div className="flex gap-2">
              {existing?.configured && !secretKey && (
                <SecondaryBlueButton label="Skip (keep existing key)" onClick={() => setStep(2)} />
              )}
              {testing ? (
                <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner />Testing…</div>
              ) : (
                <PrimaryBlueButton
                  label={secretKey ? "Test & Continue" : "Test Connection"}
                  onClick={handleTest}
                  disabled={!secretKey.trim() && !existing?.configured}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Settings ── */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
          <div>
            <h2 className="text-base font-semibold text-slate-800 mb-1">Payment settings</h2>
            <p className="text-sm text-slate-500">These apply to all payment requests sent from workflows.</p>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Display name</span>
            <input className="input" placeholder="Stripe" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Publishable Key (optional)</span>
            <input className="input font-mono text-sm" placeholder="pk_live_..." value={publishableKey} onChange={(e) => setPublishableKey(e.target.value)} />
            <span className="text-xs text-slate-400">Only needed if you embed Stripe.js directly — not required for Checkout links.</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Default success URL (optional)</span>
            <input className="input" placeholder="https://yoursite.com/payment-success" value={successUrl} onChange={(e) => setSuccessUrl(e.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Default cancel URL (optional)</span>
            <input className="input" placeholder="https://yoursite.com/payment-cancelled" value={cancelUrl} onChange={(e) => setCancelUrl(e.target.value)} />
          </label>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-700 mb-1">Payment methods on Checkout</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={enableApplePay} onChange={(e) => setEnableApplePay(e.target.checked)} className="w-4 h-4" />
              <div>
                <div className="text-sm font-medium">Apple Pay</div>
                <div className="text-xs text-slate-500">Shown automatically on iPhone/Mac Safari — no merchant registration required</div>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={enableGooglePay} onChange={(e) => setEnableGooglePay(e.target.checked)} className="w-4 h-4" />
              <div>
                <div className="text-sm font-medium">Google Pay</div>
                <div className="text-xs text-slate-500">Shown on Android and Chrome browsers</div>
              </div>
            </label>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex justify-between">
            <SecondaryBlueButton label="Back" onClick={() => setStep(1)} />
            {saving ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner />Saving…</div>
            ) : (
              <PrimaryBlueButton label="Save & Continue" onClick={async () => { await handleSave(); if (!error) setStep(3); }} />
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Webhook setup ── */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
          <div>
            <h2 className="text-base font-semibold text-slate-800 mb-1">Set up the Stripe webhook</h2>
            <p className="text-sm text-slate-500">
              This lets Velaro know when a customer completes payment — so your workflow can automatically
              continue (e.g. send a confirmation, close the conversation).
            </p>
          </div>

          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800 space-y-3">
            <p className="font-medium">Setup steps:</p>
            <ol className="list-decimal list-inside text-sm space-y-2 text-blue-700">
              <li>Go to your <strong>Stripe Dashboard → Developers → Webhooks</strong></li>
              <li>Click <strong>Add endpoint</strong></li>
              <li>Paste this URL: <code className="bg-blue-100 px-1 rounded break-all">{WEBHOOK_URL}</code></li>
              <li>Select events: <code className="bg-blue-100 px-1 rounded">checkout.session.completed</code> and <code className="bg-blue-100 px-1 rounded">checkout.session.expired</code></li>
              <li>Click <strong>Add endpoint</strong>, then copy the <strong>Signing secret</strong> (starts with whsec_)</li>
              <li>Paste it below</li>
            </ol>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Webhook Signing Secret</span>
            <input
              type="password"
              className="input font-mono text-sm"
              placeholder="whsec_..."
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
            />
            <span className="text-xs text-slate-400">
              Without this, Velaro can't verify payments are legitimate and workflows won't auto-resume.
            </span>
          </label>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex justify-between">
            <SecondaryBlueButton label="Back" onClick={() => setStep(2)} />
            <div className="flex gap-2">
              <SecondaryBlueButton label="Skip for now" onClick={() => setStep(4)} />
              {saving ? (
                <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner />Saving…</div>
              ) : (
                <PrimaryBlueButton label="Save Webhook & Finish" onClick={async () => { await handleSave(); }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Step 4: Done ── */}
      {step === 4 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-500 text-3xl">✓</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Stripe connected!</h2>
          <p className="text-sm text-slate-500 mb-2">
            Apple Pay and Google Pay are automatically enabled on Checkout for iPhone and Android users.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Add a <strong>Request Payment</strong> node to any workflow to start collecting in-chat payments.
          </p>
          <div className="flex justify-center gap-3">
            <SecondaryBlueButton label="Back to Integrations" onClick={() => navigate("/integrations")} />
            <PrimaryBlueButton label="Build a Workflow" onClick={() => navigate("/workflows")} />
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="Stripe" />
      </div>
    </div>
  );
}
